import React, { useState, useMemo } from 'react';
import { Monitor, Smartphone, ExternalLink, Download, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import api from '../../../../lib/api';
import { useChartConfigs } from '../../../../hooks/useChartConfigs';
import { getChartableFields, buildDemographicQuestions, Question } from '../../../../utils/chartableFields';
import { personalMessageExplicitlySelected, personalMessageQuestion } from '../../../../utils/personalMessageColumn';
import { aggregateResponsesByField, getAnswerValue } from '../../../../utils/analyticsUtils';
import { extractTextResponses, extractWordFrequencies } from '../../../../utils/textAnalysisUtils';
import DOMPurify from 'dompurify';
import BrandedHtmlFrame from '../../BrandedHtmlFrame';
import LogoGrid from '../../LogoGrid';
import type { ReportFormState } from './OutreachReportWizard';
import { downloadViewExport } from '../../../../hooks/useAnalyticsData';

interface Props {
  itemId: string;
  viewId: string;
  shareToken: string;
  formState: ReportFormState;
}

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6'];

type ViewMode = 'desktop' | 'mobile';

function truncateLabel(label: string, max = 20): string {
  if (!label) return '';
  return label.length > max ? label.slice(0, max - 1) + '\u2026' : label;
}

const ReportStep5Preview: React.FC<Props> = ({ itemId, viewId, shareToken, formState }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('desktop');
  const [downloading, setDownloading] = useState(false);
  const publicUrl = shareToken
    ? `${window.location.origin}/public/analytics/report/${shareToken}`
    : '';

  const handleDownload = async (format: 'pdf' | 'excel') => {
    setDownloading(true);
    try {
      await downloadViewExport(itemId, viewId, format, formState.name || 'report');
      toast.success(`Report downloaded as ${format === 'pdf' ? 'PDF' : 'Excel'}`);
    } catch (error) {
      console.error('Failed to download report:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const { charts } = useChartConfigs(itemId, viewId);

  const { data: itemData } = useQuery(
    ['outreach-item-def', itemId],
    async () => {
      const res = await api.get(`/outreach-unified/items/${itemId}`);
      return res.data.data;
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: analyticsData } = useQuery(
    ['outreach-analytics-data', itemId],
    async () => {
      const res = await api.get(`/outreach-unified/items/${itemId}/analytics-data?include=responses`);
      return res.data.data;
    },
    { staleTime: 60 * 1000 }
  );

  const branding = itemData?.definition_json?.branding;
  const primaryColor = branding?.colors?.primary || '#6366f1';
  const secondaryColor = branding?.colors?.secondary || '#64748b';
  const bgColor = branding?.colors?.background || '#ffffff';
  const textColor = branding?.colors?.text || '#1e293b';
  const headingFont = branding?.fonts?.heading || 'Inter, sans-serif';
  const bodyFont = branding?.fonts?.body || 'Inter, sans-serif';

  React.useEffect(() => {
    const fonts = Array.from(new Set([headingFont, bodyFont].filter(f => f && f !== 'Inter, sans-serif')));
    if (fonts.length === 0) return;

    document.querySelectorAll('link[data-report-preview-font]').forEach(el => el.remove());

    const families = fonts.map(f => f.replace(/ /g, '+').replace(/, sans-serif$/, '')).join('&family=');
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${families}:wght@400;500;600;700&display=swap`;
    link.setAttribute('data-report-preview-font', 'true');
    document.head.appendChild(link);

    return () => {
      document.querySelectorAll('link[data-report-preview-font]').forEach(el => el.remove());
    };
  }, [headingFont, bodyFont]);

  const questions: Question[] = useMemo(() => {
    const raw = itemData?.definition_json?.questions;
    const defined = !raw || !Array.isArray(raw) ? [] : raw.map((q: any, idx: number) => {
      const text = q.question || q.label || q.text || q.title || '';
      return {
        id: q.id || `question_${idx}`,
        label: text ? `Q${idx + 1}: ${text}` : `Question ${idx + 1}`,
        type: q.type || 'text',
        options: q.options || []
      };
    });
    return [...defined, ...buildDemographicQuestions(itemData?.definition_json)];
  }, [itemData]);

  const responses = analyticsData?.responses?.data || analyticsData?.responses || [];
  const chartableFields = useMemo(() => getChartableFields(questions, { includeAll: true }), [questions]);

  const sections = formState.sectionsJson;

  const responseColumns = sections.response_columns || [];
  const visibleQuestions = useMemo(() => {
    // Empty selection = all columns — which never includes the personal
    // message (strictly opt-in; keeps pre-existing reports identical).
    if (responseColumns.length === 0) return questions;
    let pool = questions;
    if (personalMessageExplicitlySelected(responseColumns)) {
      const pm = personalMessageQuestion() as unknown as Question;
      const demoStart = questions.findIndex(q => String(q.id).startsWith('demo_'));
      pool = demoStart === -1
        ? [...questions, pm]
        : [...questions.slice(0, demoStart), pm, ...questions.slice(demoStart)];
    }
    return pool.filter(q => responseColumns.includes(q.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, JSON.stringify(responseColumns)]);

  const getChartData = (questionId: string) => {
    const field = chartableFields.find(f => f.id === questionId);
    return aggregateResponsesByField(responses, questionId, field?.options);
  };

  const containerClass = viewMode === 'mobile'
    ? 'max-w-[375px] mx-auto border border-border-light dark:border-border-dark rounded-2xl shadow-xl overflow-hidden'
    : 'max-w-full';

  return (
    <div className="space-y-4">
      {/* View mode toggle + open link */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-white dark:bg-background-dark-secondary rounded-lg p-1">
          <button
            onClick={() => setViewMode('desktop')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'desktop'
                ? 'bg-white dark:bg-background-dark-primary text-text-primary dark:text-text-dark-primary shadow-sm'
                : 'text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary'
            }`}
          >
            <Monitor className="w-4 h-4" />
            Desktop
          </button>
          <button
            onClick={() => setViewMode('mobile')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'mobile'
                ? 'bg-white dark:bg-background-dark-primary text-text-primary dark:text-text-dark-primary shadow-sm'
                : 'text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary'
            }`}
          >
            <Smartphone className="w-4 h-4" />
            Mobile
          </button>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => handleDownload('pdf')}
            disabled={downloading}
            className="flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 disabled:opacity-50"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            PDF
          </button>
          <button
            onClick={() => handleDownload('excel')}
            disabled={downloading}
            className="flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 disabled:opacity-50"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Excel
          </button>
          {publicUrl && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
            >
              <ExternalLink className="w-4 h-4" />
              Open in new tab
            </a>
          )}
        </div>
      </div>

      {/* Preview frame */}
      <div className="bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg p-4 min-h-[500px]">
        <div className={containerClass}>
          <div className="min-h-[480px]" style={{ backgroundColor: bgColor, color: textColor, fontFamily: bodyFont }}>
            {/* Organization Logo */}
            {branding?.logos && (
              <div className="py-3 px-6" style={{ backgroundColor: bgColor }}>
                <LogoGrid logos={branding.logos} area="header" />
              </div>
            )}

            {/* Custom Header HTML */}
            {branding?.custom_header_html && (
              <BrandedHtmlFrame html={branding.custom_header_html} />
            )}

            <div className={`${viewMode === 'mobile' ? 'px-4 py-4' : 'px-6 py-6'} space-y-5`}>
              {/* Title card */}
              <div className="rounded-lg p-4" style={{ backgroundColor: `${primaryColor}08` }}>
                <h2
                  className={`font-bold ${viewMode === 'mobile' ? 'text-lg' : 'text-2xl'}`}
                  style={{ fontFamily: headingFont, color: primaryColor }}
                >
                  {formState.name}
                </h2>
                {formState.description && (
                  <p className="text-sm mt-1" style={{ color: secondaryColor }}>
                    {formState.description}
                  </p>
                )}
              </div>

              {/* Total Responses Only */}
              {sections.overview_stats && (
                <div className="rounded-lg p-4 text-center" style={{ backgroundColor: `${primaryColor}08` }}>
                  <p className={`font-bold ${viewMode === 'mobile' ? 'text-3xl' : 'text-4xl'}`} style={{ color: primaryColor }}>
                    {responses.length}
                  </p>
                  <p className="text-xs mt-1" style={{ color: secondaryColor }}>Total Responses</p>
                </div>
              )}

              {/* Summary */}
              {sections.summary && formState.summaryHtml && (
                <div className="rounded-lg p-4" style={{ backgroundColor: `${primaryColor}08` }}>
                  <h3 className="font-semibold mb-2" style={{ fontFamily: headingFont, color: textColor }}>Summary</h3>
                  <div
                    className="text-sm prose prose-sm max-w-none"
                    style={{ color: textColor }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formState.summaryHtml) }}
                  />
                </div>
              )}

              {/* Charts */}
              {sections.charts && charts.length > 0 && (
                <div className={`grid ${viewMode === 'mobile' ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                  {charts.map((chart) => (
                    <div key={chart.id} className="rounded-lg p-4" style={{ backgroundColor: `${primaryColor}08` }}>
                      <h3 className="font-semibold mb-3 text-sm" style={{ fontFamily: headingFont, color: textColor }}>
                        {chart.title || 'Chart'}
                      </h3>
                      {chart.chart_type === 'wordcloud'
                        ? renderWordCloud(responses, chart.question_id)
                        : renderPreviewChart(chart.chart_type, getChartData(chart.question_id), viewMode, primaryColor)
                      }
                    </div>
                  ))}
                </div>
              )}

              {/* Responses Table (filtered by response_columns) */}
              {sections.responses_table && responses.length > 0 && (
                <div className="rounded-lg p-4" style={{ backgroundColor: `${primaryColor}08` }}>
                  <h3 className="font-semibold mb-3" style={{ fontFamily: headingFont, color: textColor }}>
                    Responses ({Math.min(responses.length, 5)})
                  </h3>
                  <div className="overflow-x-auto text-xs">
                    <table className="w-full">
                      {/* eslint-disable-next-line no-restricted-syntax -- org-BRANDED preview: customer colors by design, not the app design system */}
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${secondaryColor}30` }}>
                          <th className="text-left py-2 px-2 font-medium" style={{ color: secondaryColor }}>Date</th>
                          {visibleQuestions.map(q => (
                            <th key={q.id} className="text-left py-2 px-2 font-medium truncate max-w-[120px]" style={{ color: secondaryColor }}>
                              {q.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {responses.slice(0, 5).map((r: any, idx: number) => (
                          <tr key={idx} style={{ borderBottom: `1px solid ${secondaryColor}15` }}>
                            <td className="py-2 px-2" style={{ color: textColor }}>
                              {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '\u2014'}
                            </td>
                            {visibleQuestions.map(q => {
                              const val = getAnswerValue(r, q.id);
                              let display: string;
                              if (!val) {
                                display = '\u2014';
                              } else if (Array.isArray(val)) {
                                display = val.map(v => q.options?.find((o: any) => o.value === v)?.label || v).join(', ');
                              } else {
                                display = q.options?.find((o: any) => o.value === val)?.label || val;
                              }
                              return (
                                <td key={q.id} className="py-2 px-2 truncate max-w-[120px]" style={{ color: textColor }}>
                                  {display}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {responses.length > 5 && (
                      <p className="text-center mt-2 text-xs" style={{ color: secondaryColor }}>
                        +{responses.length - 5} more responses shown in full report
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer with Logo + Custom Footer */}
            <div className="py-4 px-6" style={{ borderTop: `1px solid ${secondaryColor}20` }}>
              {branding?.logos && (
                <LogoGrid logos={branding.logos} area="footer" className="mb-3" />
              )}
              {branding?.custom_footer_html && (
                <BrandedHtmlFrame html={branding.custom_footer_html} />
              )}
              <div className="text-center text-xs py-2" style={{ color: secondaryColor }}>
                Powered by <span className="font-semibold">Capitol Commons PBC</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const WORD_CLOUD_COLORS = [
  '#8b5cf6', '#6366f1', '#3b82f6', '#14b8a6',
  '#22c55e', '#eab308', '#f97316', '#ef4444',
];

function renderWordCloud(responses: any[], questionId: string) {
  const texts = extractTextResponses(responses, questionId);
  const words = extractWordFrequencies(texts, { minWordLength: 3, maxWords: 30, includeStopWords: false });

  if (words.length === 0) {
    return (
      <div className="h-[120px] flex items-center justify-center text-text-tertiary dark:text-text-dark-tertiary text-xs">
        No text data available for word cloud
      </div>
    );
  }

  const maxCount = Math.max(...words.map(w => w.value), 1);
  const minCount = Math.min(...words.map(w => w.value), 1);
  const getScale = (v: number) => maxCount === minCount ? 1 : 0.7 + ((v - minCount) / (maxCount - minCount)) * 1.3;

  return (
    <div className="flex flex-wrap items-center justify-center gap-1.5 p-3 min-h-[120px]">
      {words.map((word, i) => (
        <span
          key={word.text}
          className="inline-block"
          style={{
            fontSize: `${getScale(word.value)}rem`,
            color: WORD_CLOUD_COLORS[i % WORD_CLOUD_COLORS.length],
            opacity: 0.6 + ((word.value - minCount) / (maxCount - minCount || 1)) * 0.4,
            fontWeight: word.value > maxCount * 0.7 ? 600 : 400,
          }}
          title={`${word.text}: ${word.value}`}
        >
          {word.text}
        </span>
      ))}
    </div>
  );
}

function renderPreviewChart(chartType: string, data: any[], viewMode: ViewMode, brandColor = '#8b5cf6') {
  if (!data || data.length === 0) {
    return (
      <div className="h-[180px] flex items-center justify-center text-text-tertiary dark:text-text-dark-tertiary text-xs">
        No data available
      </div>
    );
  }

  const chartData = data.slice(0, 8);
  const height = viewMode === 'mobile' ? 180 : 220;

  switch (chartType) {
    case 'bar':
      return (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={chartData} margin={{ bottom: 40 }}>
            <XAxis
              dataKey="value"
              tick={{ fontSize: 10 }}
              interval={0}
              angle={-40}
              textAnchor="end"
              height={55}
              tickFormatter={(v) => truncateLabel(v, 16)}
            />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip formatter={(value: number) => [value, 'Count']} />
            <Bar dataKey="count" fill={brandColor} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    case 'horizontal-bar':
      return (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={chartData} layout="vertical">
            <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
            <YAxis
              dataKey="value"
              type="category"
              tick={{ fontSize: 10 }}
              width={80}
              tickFormatter={(v) => truncateLabel(v, 16)}
            />
            <Tooltip />
            <Bar dataKey="count" fill={brandColor} radius={[0, 3, 3, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    case 'pie':
    case 'donut':
      return (
        <ResponsiveContainer width="100%" height={height + 40}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="count"
              nameKey="value"
              cx="50%"
              cy="40%"
              innerRadius={chartType === 'donut' ? 30 : 0}
              outerRadius={55}
              label={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={entry.value} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => [value, 'Count']} />
            <Legend
              verticalAlign="bottom"
              wrapperStyle={{ fontSize: 10, paddingTop: 8 }}
              formatter={(value) => truncateLabel(String(value), 24)}
            />
          </PieChart>
        </ResponsiveContainer>
      );
    case 'line':
      return (
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData} margin={{ bottom: 40 }}>
            <XAxis
              dataKey="value"
              tick={{ fontSize: 10 }}
              angle={-40}
              textAnchor="end"
              height={55}
              tickFormatter={(v) => truncateLabel(v, 16)}
            />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip />
            <Line type="monotone" dataKey="count" stroke={brandColor} strokeWidth={2} dot={{ fill: brandColor, r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      );
    default:
      return <div className="text-xs text-text-tertiary">Unsupported chart type</div>;
  }
}

export default ReportStep5Preview;
